import api from './api';

// ── Types ─────────────────────────────────────────────────────────────────────

export type DuplicateConfidence = 'exact' | 'high';

export interface DuplicateWineItem {
  id:           string;
  displayName:  string;
  vintageType:  string;
  vintage?:     number;
  producerName: string | null;
  needsReview:  boolean;
  active:       boolean;
  createdAt:    string;
}

export interface DuplicateGroup {
  confidence:    DuplicateConfidence;
  normalizedKey: string;
  wines:         DuplicateWineItem[];
}

export interface DuplicateSummary {
  total: number;
  exact: number;
  high:  number;
}

export interface DuplicateWinesResponse {
  summary:    DuplicateSummary;
  groups:     DuplicateGroup[];
  pagination: { page: number; limit: number; total: number };
}

// ── Merge types ───────────────────────────────────────────────────────────────

export interface MergePreflightResponse {
  canProceed: boolean;
  blockers: string[];
  inventoryItems: {
    withBalance:  number;
    zeroBalance:  number;
    toRelink:     number;
    toDeactivate: number;
  };
  wineListItems: {
    total:                  number;
    linkedToRelinkedItem:   number;
    linkedToDeactivatedItem:number;
    unlinked:               number;
    unlinkedCollision:      number;
  };
  notes?: string[];
}

export interface MergeExecuteResponse {
  merged:            boolean;
  sourceDeactivated: boolean;
  inventoryItems:    { relinked: number; deactivated: number };
  wineListItems:     { relinked: number; deactivated: number };
}

// ── Service ───────────────────────────────────────────────────────────────────

const BASE        = '/admin/governance/duplicate-wines';
const MERGE_BASE  = '/admin/governance/wine-merge';

const duplicateWineService = {
  getGroups: async (
    confidence?: DuplicateConfidence,
    page  = 1,
    limit = 50,
  ): Promise<DuplicateWinesResponse> => {
    const params: Record<string, string | number> = { page, limit };
    if (confidence) params.confidence = confidence;
    const res = await api.get<DuplicateWinesResponse>(BASE, { params });
    return res.data;
  },

  preflight: async (sourceId: string, targetId: string): Promise<MergePreflightResponse> => {
    const res = await api.post<MergePreflightResponse>(`${MERGE_BASE}/preflight`, { sourceId, targetId });
    return res.data;
  },

  execute: async (sourceId: string, targetId: string): Promise<MergeExecuteResponse> => {
    const res = await api.post<MergeExecuteResponse>(`${MERGE_BASE}/execute`, { sourceId, targetId });
    return res.data;
  },
};

export default duplicateWineService;
